// @flow
import React from 'react';
import injectSheet from 'react-jss';
import getResponsiveImageUrl from 'shared/lib/getResponsiveImageUrl';

type HeroImageUrlProps =
  | {
      mobile: string,
      tablet: string,
      dekstop: string,
    }
  | string;

type Post = {
  template: {
    props: {
      url: string,
      region: 'US' | 'CN',
      heroImageUrl: HeroImageUrlProps,
      responsive_image: ?boolean
    }
  }
}

type Props = {
  post: Post,
  classes: Object
}

const FeaturedPost = (props: Props) => {
  const { post, classes: c } = props;
  const { props: postProps } = post.template;

  return (
    <a className={c.imageLink} href={postProps.url}>
      <div className={c.image} />
    </a>
  );
};

// Update image URL based on region
const regionalImage = (props) => {
  const { post: { template: { props: postProps }}} = props;

  const heroImageUrl = postProps.region === 'US' ?
    postProps.heroImageUrl :
    postProps.heroImageUrl.replace('genesis-cdn.ff', 'genesis-cdn.faradayfuturecn');

  return heroImageUrl;
};

// Update image URL based on region and generate responsive URL
const responsiveRegionalImage = (props: Props, size) => {
  const { post: { template: { props: postProps }}} = props;
  const { responsive_image: isResponsive } = postProps;

  const heroImageUrl = regionalImage(props);

  if (!isResponsive) {
    return `url(${heroImageUrl})`;
  }

  return getResponsiveImageUrl(heroImageUrl, size);
};

// Use a separate function for images using the cropper tool
const croppedRegionalImage = (props) => {
  const { post: { template: { props: postProps }}} = props;
  // Use the desktop image
  return postProps.region === 'US' ?
    postProps.heroImageUrl.desktop :
    postProps.heroImageUrl.desktop.replace('genesis-cdn.ff', 'genesis-cdn.faradayfuturecn');
};

const croppedImage = (props) => {
  const heroImageUrl = croppedRegionalImage(props);
  return `url(${heroImageUrl})`;
};

const styles = {
  image: {
    width: '100%',
    height: '100%',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center center',
    backgroundSize: 'cover',
    backgroundImage: props => props?.post?.template?.props?.heroImageUrl?.desktop ?
      croppedImage(props) : responsiveRegionalImage(props, '800w'),
    '@media(min-width: 801px)': {
      backgroundImage: props => props?.post?.template?.props?.heroImageUrl?.desktop ?
        croppedImage(props) : responsiveRegionalImage(props, '1200w'),
    },
    '@media(min-width: 1201px)': {
      backgroundImage: props => props?.post?.template?.props?.heroImageUrl?.desktop ?
        croppedImage(props) : responsiveRegionalImage(props, '1600w'),
    },
    '@media(min-width: 1601px)': {
      backgroundImage: props => !props?.post?.template?.props?.heroImageUrl?.desktop && 
        responsiveRegionalImage(props, '1800w'),
    },
    '@media(min-width: 1801px)': {
      backgroundImage: props => !props?.post?.template?.props?.heroImageUrl?.desktop &&
        `url(${regionalImage(props)})`
    },
    '@media(max-width: 960px)': {
      height: '60vh'
    },
    '@media(max-height: 701px) and (max-width: 960px)': {
      maxHeight: '400px',
      minHeight: '400px'
    }
  },
  imageLink: {
    top: 0,
    left: 0,
    height: '75vh',
    minHeight: '560px', /* Keep min-height the same as FutureSightLanding - heroSection */
    width: '100%',
    display: 'block !important',
    '@media(max-width: 960px)': {
      height: '60vh',
      minHeight: '100%'
    },
    '@media(max-height: 701px) and (max-width: 960px)': {
      maxHeight: '400px',
      minHeight: '400px'
    }
  }
};

export default injectSheet(styles)(FeaturedPost);
